// Organigramme — soft-island port of the design prototype
// (design Organigramme.html, option D « Vue focus », org-focus.jsx).
// The page owns the data, the focused person (mirrored in ?p=) and the
// re-parenting mutations; FocusView owns the reading experience.
//
// The chart reads the live roster and a single new field on the fiche,
// Employee.managerId — see server/src/routes/org.ts for the tree rules
// (entity scope, cycle guard).
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useUiStore } from '../store/ui.js';
import { toast } from '../store/toast.js';
import { useEntities, useMe, useOrg, useSetManager, type EntityDTO } from '../api/hooks.js';
import { FocusView } from '../components/org/FocusView.js';
import { orgDesc, orgIndex } from '../components/org/common.js';
import '../styles/organigramme.css';

export function OrganigrammePage() {
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const entFilter = useUiStore((s) => s.currentEntityId);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const me = useMe();
  const canEdit = (me.data?.permissions ?? []).includes('employees.edit');
  const org = useOrg();
  const entities = useEntities();
  const setManager = useSetManager();

  const nodes = useMemo(() => org.data?.nodes ?? [], [org.data]);
  const entMap = useMemo(
    () => new Map<string, EntityDTO>((entities.data ?? []).map((e) => [e.id, e])),
    [entities.data],
  );

  // Default focus = the root carrying the largest team (the PDG once the
  // chart is built, the first name alphabetically while it is empty).
  const defaultFocus = useMemo(() => {
    if (nodes.length === 0) return null;
    const { byParent, roots } = orgIndex(nodes);
    const best = roots
      .map((id) => ({ id, size: orgDesc(id, byParent).size }))
      .sort((a, b) => b.size - a.size)[0];
    return best?.id ?? nodes[0]!.id;
  }, [nodes]);

  const urlFocus = searchParams.get('p');
  const focusId = urlFocus && nodes.some((n) => n.id === urlFocus) ? urlFocus : defaultFocus;

  const setFocus = (id: string) => {
    const p = new URLSearchParams(searchParams);
    p.set('p', id);
    setSearchParams(p, { replace: true });
  };

  const attached = nodes.filter((n) => n.managerId).length;

  const onSetManager = (id: string, managerId: string | null) => {
    setManager.mutate(
      { id, managerId },
      {
        onSuccess: () => toast.success(managerId ? 'Rattachement mis à jour.' : 'Personne détachée.'),
        onError: () => toast.danger('Rattachement impossible.'),
      },
    );
  };

  // Bulk attach from the team block: one PATCH per person, reported once.
  const onAttach = async (ids: string[], managerId: string) => {
    let ok = 0;
    for (const id of ids) {
      try {
        await setManager.mutateAsync({ id, managerId });
        ok += 1;
      } catch {
        /* counted below */
      }
    }
    if (ok === ids.length) toast.success(`${ok} personne${ok > 1 ? 's' : ''} rattachée${ok > 1 ? 's' : ''}.`);
    else toast.warning(`${ok}/${ids.length} rattachement(s) effectué(s).`);
  };

  return (
    <div className={'orgapp' + (themeDark ? ' theme-dark' : '')}>
      <header className="org-top">
        <div className="org-top-id">
          <div className="org-eyebrow">Données · Organisation</div>
          <h1 className="org-title">Organigramme <em>du groupe</em></h1>
        </div>
        <div className="org-top-stats" data-testid="org-stats">
          <span><b>{nodes.length}</b> personnes</span>
          <span><b>{attached}</b> rattachées</span>
          <span className={nodes.length - attached > 0 ? 'warn' : ''}>
            <b>{nodes.length - attached}</b> sans manager
          </span>
        </div>
      </header>

      <div className="org-body">
        {org.isLoading && <p className="org-state">Chargement de l'organigramme…</p>}
        {org.isError && <p className="org-state" data-testid="org-error">Impossible de charger l'organigramme.</p>}
        {org.data && nodes.length === 0 && (
          <p className="org-state">Aucun employé sur le périmètre visible.</p>
        )}
        {org.data && focusId && (
          <FocusView
            nodes={nodes}
            entities={entMap}
            focusId={focusId}
            onFocus={setFocus}
            query={query}
            onQuery={setQuery}
            entFilter={entFilter}
            canEdit={canEdit}
            busy={setManager.isPending}
            onSetManager={onSetManager}
            onAttach={onAttach}
            onOpenFiche={(id) => navigate(`/employees?emp=${id}`)}
          />
        )}
      </div>
    </div>
  );
}
